import { useMemo, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { Source, SourceItem, SourceItemPage } from '@mg/shared';
import { SOURCE_ITEMS_PAGE_SIZE } from '@mg/shared';
import { api, ApiRequestError } from '../../lib/api';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import { MediaSearchBox } from '../media/MediaSearchBox';
import { LoadMoreButton } from '../media/LoadMoreButton';
import { SourceItemList } from './SourceItemList';

/**
 * Read-only viewer for the items ingested from a single source — the stored
 * articles behind the "Total items" count. Newest first, a page at a time
 * ("Load more"), with a search over the title, summary, author and link.
 */
export function SourceItemsModal({
  source,
  onClose,
}: {
  source: Source | undefined;
  onClose: () => void;
}) {
  return (
    <Modal
      open={source != null}
      onClose={onClose}
      title={source ? `Items · ${source.name}` : 'Items'}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {/* Keyed by source, so the next one opens with an empty search and its own first page. */}
      {source && <SourceItems key={source.id} source={source} />}
    </Modal>
  );
}

function SourceItems({ source }: { source: Source }) {
  /** The search in force, trimmed. */
  const [search, setSearch] = useState('');

  const items = useInfiniteQuery({
    queryKey: ['source-items', source.id, search],
    queryFn: ({ pageParam, signal }) =>
      api.listSourceItemsPage(
        source.id,
        { q: search, cursor: pageParam, limit: SOURCE_ITEMS_PAGE_SIZE },
        signal,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last: SourceItemPage) => last.nextCursor ?? undefined,
    // Fetch the latest on every open.
    staleTime: 0,
  });

  const pages = items.data?.pages;
  /** Every loaded page in order, each item once (a pull between two pages can repeat one). */
  const list = useMemo(() => {
    const seen = new Set<string>();
    const out: SourceItem[] = [];
    for (const page of pages ?? []) {
      for (const item of page.items) {
        if (seen.has(item.id)) continue;
        seen.add(item.id);
        out.push(item);
      }
    }
    return out;
  }, [pages]);
  const total = Math.max(pages?.length ? pages[pages.length - 1].total : 0, list.length);
  // Loaded items stay on screen through a failed "Load more" and a failed re-read (the list goes on
  // holding its last error while it is read again); the error shows only when there are none.
  const failed = items.isError && list.length === 0;

  return (
    <div className="space-y-3">
      <MediaSearchBox value={search} onSearch={setSearch} label="Search items" />
      {items.isLoading ? (
        <LoadingState label="Loading items…" />
      ) : failed ? (
        <ErrorState
          message={
            items.error instanceof ApiRequestError ? items.error.message : 'Could not load items.'
          }
          onRetry={() => void items.refetch()}
        />
      ) : list.length === 0 ? (
        search ? (
          <EmptyState
            title="No items match your search"
            description="Try a different word, or clear the search."
          />
        ) : (
          <EmptyState
            title="No items yet"
            description="This source hasn't ingested any items. Try “Pull now” to fetch the latest."
          />
        )
      ) : (
        <>
          <p className="text-xs text-ink-faint">
            Showing {list.length} of {total} item{total === 1 ? '' : 's'}
          </p>
          <SourceItemList items={list} />
          {items.hasNextPage && (
            <LoadMoreButton
              size="sm"
              loading={items.isFetchingNextPage}
              failed={items.isFetchNextPageError}
              onClick={() => void items.fetchNextPage()}
            />
          )}
        </>
      )}
    </div>
  );
}
